import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag, ImportWizard } from './ui';
import {
  useC360List, C360Table, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/shipmentvehicles';

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Bakımda', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const GROUP_OPTIONS = [
  { value: 'vendorId', label: 'Distribütör' },
  { value: 'warehouseId', label: 'Depo' },
  { value: 'status', label: 'Durum' },
];

const GROUP_BY_ODATA = {
  vendorId: { field: 'VendorId', type: 'guid' },
  warehouseId: { field: 'WarehouseId', type: 'guid' },
  status: { field: 'Status', type: 'number' },
};

const COLUMN_FILTER_META = {
  plaka: { type: 'text', odataField: 'Plaka' },
  brand: { type: 'text', odataField: 'Brand' },
  model: { type: 'text', odataField: 'Model' },
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  warehouseLabel: { type: 'select', odataField: 'WarehouseId', valueFormat: 'guid' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};

const SEARCH_FIELDS = ['Plaka', 'Brand', 'Model'];

// Liste/Kanban/Grafik + Kart görünümü. Kart, Liste'nin hemen YANINDA (index 1).
const SHIPMENT_VEHICLE_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const toNum = (v) => (v === '' || v == null ? null : Number(v));
const fmtNum = (v) => (v == null || v === '' ? '-' : Number(v).toLocaleString('tr-TR'));

// === Sevkiyat Araçları Grafik görünümü ===
function ChartView({ rows, getVendorLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => Number(r.status) === 1).length;
  const vendorSet = new Set(rows.map((r) => r.vendorId).filter(Boolean));
  const warehouseSet = new Set(rows.map((r) => r.warehouseId).filter(Boolean));

  const vendorCounts = {};
  rows.forEach((r) => {
    const lbl = r.vendorId ? getVendorLabel(r.vendorId) : 'Atanmamış';
    vendorCounts[lbl] = (vendorCounts[lbl] || 0) + 1;
  });
  const vendorData = Object.entries(vendorCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);

  const statusPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const statusCounts = {};
  rows.forEach((r) => {
    const k = r.status == null || r.status === '' ? '__none__' : Number(r.status);
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([k, v]) => {
    const meta = STATUS_META[k];
    return { name: meta?.label || 'Bilinmiyor', value: v, color: meta ? statusPalette[meta.tone] : '#cbd5e1' };
  });

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Araç" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif Araç" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Distribütör" value={vendorSet.size.toLocaleString('tr-TR')} hint="farklı distribütör" />
        <KpiCard label="Depo" value={warehouseSet.size.toLocaleString('tr-TR')} hint="farklı depo" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Distribütöre Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {vendorData.length} distribütör</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={vendorData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} araç`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} araç`} data={statusData} />
      </div>
    </div>
  );
}

export function ShipmentVehiclesPage() {
  usePageMeta('Sevkiyat Araçları', 'Sevkiyat araç tanımlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // #770.4 Araç kullanımdaysa backend'in sorduğu onay ({row, message}); null = sorulmuyor.
  const [passivateAsk, setPassivateAsk] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getVendorLabel = useCallback((id) => {
    const v = vendors.find((item) => item.id === id);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);

  const getWarehouseLabel = useCallback((id) => {
    const w = warehouses.find((item) => item.id === id);
    if (!w) return '-';
    return w.name ? `${w.textId || ''} - ${w.name}`.trim().replace(/^- /, '') : w.textId || '-';
  }, [warehouses]);

  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() }).then((res) => res.ok ? res.json() : []).catch(() => []),
      fetch('/api/warehouses/Select', { headers: authHeaders() }).then((res) => res.ok ? res.json() : []).catch(() => []),
    ])
      .then(([vData, wData]) => { setVendors(unwrapLookupItems(vData)); setWarehouses(unwrapLookupItems(wData)); })
      .finally(() => setLookupsLoaded(true));
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = field === 'status' ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
    else if (field === 'vendorId') label = getVendorLabel(key);
    else if (field === 'warehouseId') label = getWarehouseLabel(key);
    else label = String(key);
    return { key, label };
  }, [getVendorLabel, getWarehouseLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'status') return Object.entries(STATUS_META).map(([k, meta]) => ({ key: Number(k), label: meta.label }));
    if (groupField === 'vendorId') return vendors.map((v) => ({ key: v.id, label: getVendorLabel(v.id) }));
    if (groupField === 'warehouseId') return warehouses.map((w) => ({ key: w.id, label: getWarehouseLabel(w.id) }));
    return [];
  }, [vendors, warehouses, getVendorLabel, getWarehouseLabel]);

  // $select: yalnız görünür kolonların OData alanları + kritikler. Volume/MaxWeight
  // kolonları COLUMN_FILTER_META'da yok (filtresiz) ama listede row'dan okunuyor → always'e ekli.
  // sortField $select'e katılmaz: $orderby projeksiyondan önce tam entity'ye uygulanır (güvenli).
  // needsFullData (grup/kanban/chart) hook içinde ele alınır → burada verilmez.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'Volume', 'MaxWeight'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Plaka',
    defaultKanbanGroup: 'status',
    getKeyLabel,
    seedKanbanBuckets,
    selectFields,
    ready: lookupsLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  const openNew = () => navigate('/shipment-vehicles/new');
  const openDetail = (row) => { if (!row) return; navigate(`/shipment-vehicles/${row.id}`, { state: { row } }); };
  const openEditRow = (row) => { if (!row) return; navigate(`/shipment-vehicles/${row.id}?edit=1`, { state: { row } }); };

  /**
   * #770.4 (2. tur) İKİ AŞAMALI SİLME — depo silme (#769.4) ile aynı sözleşme.
   *
   * Aracın hareketi varsa onaysız ilk çağrıda backend hiçbir şey yazmaz, `needsPassivateConfirm`
   * ile sorar. Eskiden sormadan pasife alıyor ve sonucu kırmızı hata tonuyla bildiriyordu; sahada
   * "kayıt silinemez uyarısı ekrana gelmedi" olarak görüldü.
   */
  const handleDelete = async (confirmPassivate = false) => {
    const target = deleteConfirm || passivateAsk?.row;
    if (!target) return;
    try {
      const qs = confirmPassivate ? '?confirmPassivate=true' : '';
      const res = await fetch(`${API_BASE}/Delete${qs}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: target.id }) });
      const body = await res.json().catch(() => null);
      // Backend'in mesajını GÖSTER — kullanıcı neden silinemediğini öğrenebilmeli.
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');

      const data = body?.data ?? body?.Data;
      if (data && (data.needsPassivateConfirm ?? data.NeedsPassivateConfirm)) {
        setPassivateAsk({
          row: target,
          message: body?.message || body?.Message
            || 'Bu araç kullanımda olduğu için silinemez. Pasife alınsın mı?',
        });
        setDeleteConfirm(null);
        return;
      }

      if (data && (data.passivated ?? data.Passivated)) {
        showToast('info', body?.message || body?.Message || 'Araç silinmedi, pasife alındı.');
        setPassivateAsk(null);
        setDeleteConfirm(null);
        setSelected(null);
        fetchData();
        return;
      }

      showToast('success', 'Araç başarıyla silindi');
      setDeleteConfirm(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(null);
      setPassivateAsk(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('plaka', {
      header: 'Plaka', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        return <span className="c360-customer-cell"><AvatarBadge name={v} tone="customer" /><span className="c360-cell-main">{v}</span></span>;
      },
    }),
    columnHelper.accessor('brand', { header: 'Marka', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('model', { header: 'Model', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('volume', { header: 'Hacim', enableColumnFilter: false, cell: (info) => fmtNum(info.getValue()) }),
    columnHelper.accessor('maxWeight', { header: 'Max Ağırlık', enableColumnFilter: false, cell: (info) => fmtNum(info.getValue()) }),
    columnHelper.accessor((row) => getVendorLabel(row.vendorId), {
      id: 'vendorLabel', header: 'Distribütör', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor((row) => getWarehouseLabel(row.warehouseId), {
      id: 'warehouseLabel', header: 'Depo', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="neutral">{v}</PillTag>; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined || v === '') return '-';
        const meta = STATUS_META[v] ?? { label: String(v), tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getVendorLabel, getWarehouseLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
    warehouseLabel: warehouses.map((w) => ({ value: w.id, label: w.name ? `${w.textId || ''} - ${w.name}`.replace(/^- /, '') : (w.textId || w.id) })),
  }), [vendors, warehouses]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options || lookupOptions[columnId] }), [lookupOptions]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Plaka', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'plaka', headerName: 'Plaka' },
        { field: 'brand', headerName: 'Marka' },
        { field: 'model', headerName: 'Model' },
        { field: 'volume', headerName: 'Hacim' },
        { field: 'maxWeight', headerName: 'Max Ağırlık' },
        { headerName: 'Distribütör', valueGetter: ({ row }) => getVendorLabel(row.vendorId) },
        { headerName: 'Depo', valueGetter: ({ row }) => getWarehouseLabel(row.warehouseId) },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `sevkiyat_araclari_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma === (Distribütör ve Depo, kod ile eşlenir)
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const importRowToPayload = (row) => {
    const vendorCode = importPick(row, ['Distribütör Kodu', 'Distributor Kodu', 'Distribütör', 'vendorTextId']);
    const warehouseCode = importPick(row, ['Depo Kodu', 'Depo', 'warehouseTextId']);
    const vendor = vendors.find((v) => (v.textId || '').toLowerCase() === vendorCode.toLowerCase());
    const warehouse = warehouses.find((w) => (w.textId || '').toLowerCase() === warehouseCode.toLowerCase());
    return {
      plaka: importPick(row, ['Plaka', 'plaka']),
      brand: importPick(row, ['Marka', 'brand']),
      model: importPick(row, ['Model', 'model']),
      volume: importPick(row, ['Hacim', 'volume']),
      maxWeight: importPick(row, ['Max Ağırlık', 'Max Agirlik', 'maxWeight']),
      vendorId: vendor?.id || null,
      warehouseId: warehouse?.id || null,
      _vendorCode: vendorCode,
      _warehouseCode: warehouseCode,
    };
  };
  const importValidateRow = (p) => {
    if (!p.plaka) return 'Plaka boş';
    if (!p.vendorId) return p._vendorCode ? `Distribütör bulunamadı: ${p._vendorCode}` : 'Distribütör Kodu boş';
    if (p._warehouseCode && !p.warehouseId) return `Depo bulunamadı: ${p._warehouseCode}`;
    return null;
  };
  const importSubmitRow = async (p) => {
    const payload = { plaka: p.plaka, brand: p.brand || null, model: p.model || null, volume: toNum(p.volume), maxWeight: toNum(p.maxWeight), status: 1, vendorId: p.vendorId, warehouseId: p.warehouseId || null };
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Plaka': '34ABC123', 'Marka': 'Ford', 'Model': 'Transit', 'Hacim': '12', 'Max Ağırlık': '3500', 'Distribütör Kodu': 'V001', 'Depo Kodu': '' }], null, 'sevkiyat_araci_sablon');
  };

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Plaka, marka veya model ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        {canAdd && <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>}
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Sevkiyat Araçları"
        subtitle="Sevkiyat araç tanımlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={SHIPMENT_VEHICLE_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Araç</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getVendorLabel={getVendorLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const rowStatusMeta = STATUS_META[row.status];
                        const isV = (id) => columnVisibility[id] !== false;
                        const vendorLbl = getVendorLabel(row.vendorId);
                        const warehouseLbl = getWarehouseLabel(row.warehouseId);
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openDetail(row)}>
                            {isV('plaka') && (
                              <div className="c360-kanban-card-head">
                                {row.plaka && <AvatarBadge name={row.plaka} tone="customer" size={28} />}
                                <span className="c360-kanban-card-title" title={row.plaka || ''}>{row.plaka || '-'}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              {isV('brand') && row.brand && <span className="c360-erp-chip">{row.brand}{row.model ? ` ${row.model}` : ''}</span>}
                              {isV('status') && rowStatusMeta && <StatusBadge tone={rowStatusMeta.tone}>{rowStatusMeta.label}</StatusBadge>}
                              {isV('vendorLabel') && vendorLbl && vendorLbl !== '-' && <span className="c360-erp-chip" title="Distribütör">{vendorLbl}</span>}
                              {isV('warehouseLabel') && warehouseLbl && warehouseLbl !== '-' && <span className="c360-erp-chip" title="Depo">{warehouseLbl}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const meta = STATUS_META[row.status];
              const vendorLbl = getVendorLabel(row.vendorId);
              const warehouseLbl = getWarehouseLabel(row.warehouseId);
              const brandModel = [row.brand, row.model].filter(Boolean).join(' ');
              return {
                avatarName: row.plaka,
                avatarTone: 'customer',
                title: row.plaka,
                subtitle: brandModel || undefined,
                statusTone: meta?.tone,
                statusLabel: meta?.label,
                meta: [
                  vendorLbl && vendorLbl !== '-' ? { k: 'Distribütör', v: vendorLbl } : null,
                  warehouseLbl && warehouseLbl !== '-' ? { k: 'Depo', v: warehouseLbl } : null,
                  (row.volume != null && row.volume !== '') ? { k: 'Hacim', v: fmtNum(row.volume) } : null,
                  (row.maxWeight != null && row.maxWeight !== '') ? { k: 'Max Ağırlık', v: fmtNum(row.maxWeight) } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openDetail },
              { kind: 'edit', title: 'Değiştir', onClick: openEditRow, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => setDeleteConfirm(r), hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{deleteConfirm?.plaka}"</strong> aracını silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete(false)}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* #770.4 İkinci aşama: araç kullanımda. Pasife alma kararı kullanıcınındır. */}
      {passivateAsk && (
        <div style={s.overlay} onClick={() => setPassivateAsk(null)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={s.modalTitle}>Araç Kullanımda</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>{passivateAsk.message}</p>
              <p style={{ margin: '10px 0 0', color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: 13 }}>
                Pasife alınan araç listede kalır ve geçmiş kayıtları korunur; yeni sevkiyat
                planlarında seçilemez. Vazgeçerseniz hiçbir şey değişmez.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={() => handleDelete(true)}>Evet, Pasife Al</button>
              <button style={s.btnSecondary} onClick={() => setPassivateAsk(null)}>Vazgeç</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} araç içe aktarıldı`); fetchData(); } }}
        title="Sevkiyat Aracı İçe Aktar"
        helpText="Zorunlu alanlar: Plaka ve Distribütör Kodu (mevcut bir distribütör koduyla eşleşmeli). Depo Kodu opsiyoneldir."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
